/**
 *  payplus/soapClient.js is intended to serve as a Node.js wrapper for the PayPlus SOAP Client
 *  for storing, retrieving and making payments with credit/debit cards
 *
 *  Available Methods:
 *
 *  - AuthCreditCardFull
 *  - AuthCreditCardFullEx
 *  - RetryAuthCreditCardFull
 *  - RemoveLastTransaction
 *  - RemoveTransactionsByVoucherId
 *  - RestoreAuthCreditCardFullByVoucherId
 *  - GetDepositData
 *  - RetainerCreditCardStorage
 *  - DepositTransactionsEx
 */

'use strict';
const config 			        = require('config');
const camelcaseKeys 	        = require('camelcase-keys');
const moment 			        = require('moment');
const numeral                   = require('numeral');
const soap 				        = require('soap');
const Joi                       = require('joi');
const transactionResultCodes    = require('./errors/transactions.json');
const retainerResultCodes       = require('./errors/retainer.json');

class InvalidParametersError extends Error {
    constructor(message) {
        super(message);
        this.name = 'InvalidParametersError';
    }
}

const cardTypeRegexDict = {
    '^4[0-9]{12}(?:[0-9]{3})?$' : 'visa',
    '^3[47][0-9]{13}$' : 'amex',
    '^6(?:011|5[0-9]{2})[0-9]{12}$' : 'discover',
    '^(?:5[1-5][0-9]{2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12}$' : 'mastercard',
};

/**
 * Infer the card type from the card number
 * @param cardNum The card number (e.g 1234 5678 9102 3456)
 * @returns {string} One of the following: visa, amex, discover, mastercard
 */
function getCardType(cardNum) {
    for(let regex in cardTypeRegexDict) {
        if(!cardNum.match(regex)) {
            continue;
        }
        return cardTypeRegexDict[regex];
    }
    return 'unknown';
}

/**
 * Remove leading zeros from a given card number
 * @param cardNum The card number
 * @returns {string} un-padded card number
 */
function removeCardPadding(cardNum) {
    // language=JSRegexp
    return cardNum.replace(/^[0]+/g, '');
}

/**
 * Pad a credit card number with leading zeros
 * @param cardNum The card number
 * @return {string} A 19-digit padded credit card
 */
function padCreditCard(cardNum) {
    const paddingAmount = 19;

    let padded = '';
    for(let i = paddingAmount; i > cardNum.length; i--) {
        padded += '0';
    }
    padded += cardNum;
    return padded;
}

const intOpConfiguration = {
    cardBrand: {
        "0" : "PL",
        "1" : "MasterCard",
        "2" : "Visa",
        "3" : "Maestro",
        "5" : "Isracard",
    },
    clearerId: {
        "1" : "Isracard",
        "2" : "Visa CAL",
        "3" : "Diners",
        "4" : "American Express",
        "6" : "Leumi Card"
    },
    ccCompanyResultForId: {
        "0" : "Not Inserted",
        "1" : "Valid",
        "2" : "Invalid",
        "3" : "Unchecked"
    },
    ccCompanyResultForCVV: {
        "0" : "Not Inserted",
        "1" : "Valid",
        "2" : "Invalid",
        "3" : "Unchecked"
    },
    ccCompanyCode: {
        "1" : "Isracard",
        "2" : "Visa CAL",
        "3" : "Diners",
        "4" : "American Express",
        "5" : "JCB",
        "6" : "Leumi Card"
    },
    dealType: {
        "00" : "Blocked Card",
        "01" : "Regular Compulsory Transaction",
        "02" : "Approved Compulsory Transaction",
        "03" : "Forced Transaction",
        "51" : "Right Transaction",
        "52" : "Cancellation Transaction",
        "53" : "Approved Right Transaction",
    },
    connectionReasonCode: {
        " " : "No Connection Made",
        "1" : "Random Connection",
        "2" : "Connection Made - Cap",
        "3" : "Connection Made - Secret Number",
        "4" : "Connection Made - Magnetic Stripe Service Code",
        "5" : "Connection Made - Terminal Check Request",
        "6" : "Connection Made - Indecisive Blockage",
        "7" : "Connection Made - Zero Cap",
        "8" : "Connection Made - Manually Initiated Request"
    },
    confirmationSource: {
        "1" : "Confirmed by SHVA",
        "2" : "Confirmed by CC Company",
        "3" : "Confirmed by Voice Mail",
        "0" : "Unconfirmed"
    }
};

function getIntOp(intOpString) {
    function intOp(start, end) {
        return intOpString.substring(start - 1, end - 1);
    }

    let status = intOp(1, 4);
    // let displayOrder = intOp(4, 5);
    let cardNum = intOp(5, 24);
    let cardBrand = intOp(24, 25);
    let clearerId = intOp(25, 26);
    // let serviceCode = intOp(26, 29);
    // let paramJ = intOp(29, 30);
    let expDate_MMYY = intOp(30, 34);
    let ccCompanyResultForId = intOp(34, 35);
    let ccCompanyResultForCVV = intOp(35, 36);
    let amountChargedAgorot = intOp(36, 44);
    // let extraData = intOp(44, 52);
    // let amountChargedCochavim = intOp(52, 60);
    let ccCompanyCode = intOp(60, 61);
    let dealType = intOp(61, 63);
    // let creditType = intOp(63, 64);
    // let resultForUcafCavv = intOp(64, 65);
    // let currencyType = intOp(65, 66);
    let transactionCode = intOp(66, 68);
    // let clubCode = intOp(68, 69);
    let connectionReasonCode = intOp(69, 70);
    let confirmationSource = intOp(70, 71);
    let confirmationCode = intOp(71, 78);
    // let firstPaymentAmount = intOp(78, 86);
    // let constPaymentAmount = intOp(86, 94);
    // let numOfPayments = intOp(94, 96);
    // let fileNumber = intOp(96, 98);
    // let registerNumber = intOp(98, 101);
    // let resgiterIdNumber = intOp(101, 104);
    let cardName = intOp(104, 119);
    let isInternationalCard = intOp(119, 120);
    // let userData = intOp(120, 139);

    return {
        status: parseInt(status),
        card: {
            cardName: cardName,
            cardNumber: cardNum.length >= 4 ? cardNum.substring(cardNum.length - 4, cardNum.length) : 'N/A',
            expMonth: expDate_MMYY.substring(0, 2),
            expYear: expDate_MMYY.substring(2, 4),
            international: (isInternationalCard === "1")
        },
        cardBrand: intOpConfiguration.cardBrand[cardBrand.toString()],
        cardClearer: intOpConfiguration.clearerId[clearerId.toString()],
        cardIssuer: intOpConfiguration.ccCompanyCode[ccCompanyCode.toString()],
        idStatus: intOpConfiguration.ccCompanyResultForId[ccCompanyResultForId.toString()],
        cvvStatus: intOpConfiguration.ccCompanyResultForCVV[ccCompanyResultForCVV.toString()],
        transaction: {
            amount: numeral(amountChargedAgorot).value(),
            type: intOpConfiguration.dealType[dealType.toString()],
            transactionCode: numeral(transactionCode).value()
        },
        connectionStatus: intOpConfiguration.connectionReasonCode[connectionReasonCode],
        confirmationStatus: intOpConfiguration.confirmationSource[confirmationSource.toString()],
        confirmationCode: numeral(confirmationCode).value()
    };

}

function getFormattedResponseForAuthCard(result) {

    let formattedIntOp = getIntOp(result.resultRecord);
    formattedIntOp.transactionRecord = result.transactionRecord
    let retainerResult = result.valueEx3;

    function retainer(start, end) {
        return retainerResult.substring(start, end);
    }

    // ValueEx3 returned a non empty string from the retainer function
    let retainerStatusCode, retainerCardLast4, retainerCardExpDate
    if(retainerResult && retainerResult.length) {
        retainerStatusCode = retainer(0, 3);
        retainerCardLast4 = retainer(3, 7);
        retainerCardExpDate = retainer(7, 11);
    }

    if(retainerStatusCode > 0 /* Retainer Error */) {
        let errorMessage = retainerResultCodes[retainerStatusCode.toString()];
        return {
            status: retainerStatusCode,
            retainerError: errorMessage
        };
    }

    return formattedIntOp;
}

/**
 * Constructs a new PayPlusClient from a given SOAP client
 * @param soapClient A SOAP client, generated using soap for node.js
 * @param {object} ppOptions An options object. must contain: user, password, cashierNumber, merchantNumber
 * @constructor
 */
function PayPlusClient(soapClient, ppOptions) {
    this.client = soapClient;
    this.user = ppOptions.user;
    this.password = ppOptions.password;
    this.cashierNumber = ppOptions.cashierNumber;
    this.merchantNumber = ppOptions.merchantNumber;
}

// Options:
// - cvv2
// - id
// - token
// - amount (In Agorot)
// - expanded
// - protectRetry
PayPlusClient.prototype.authCreditCardEx = function(options, callback) {

    let user = this.user,
        password = this.password,
        cashierNum = this.cashierNumber,
        merchantNum = this.merchantNumber;

    let date = moment();
    let transactionDate_yyyyMMdd = date.format('YYYYMMDD');
    let transactionTime_HHmm = date.format('HHmm');
    let requestCardKey = `K${options.token}`; // As per PayPlus documentation on making payment with GUID token

    let args = {
        isUseRetry : options.protectRetry ? '1' : '0',
        premiumUserName : user,
        premiumPassword : password,
        premiumCashierNum : cashierNum,
        merchantNumber : merchantNum,
        transactionDate_yyyyMMdd : transactionDate_yyyyMMdd,
        transactionTime_HHmm : transactionTime_HHmm,
        cardNum : '',
        expDate_YYMM : '',
        amount : options.amount,
        transactionType : '01',
        creditTerms : '1',
        currency : '1',
        code : '51',
        paramJ : options.paramJ.toString() || '2', // Default behaviour is to not process the transaction, only check the payment details
        uniqueTransactionNumber_SixDigits: '',
        // Unused parameters
        cochavAmount : '',
        authNum: '',
        cvv2 : options.cvv2.toString() || '0',
        id : options.id.toString() || '000000000',
        track2 : requestCardKey,
        firstAmount : '',
        nonFirstAmount : '',
        numOfPayment : '',
        sapakMutav : '',
        sapakMutavNo : '',
        uniqNum : '',
        clubCode : '',
        addData : '',
        eci : '',
        cavvUcaf : '',
        last4Digits : '',
        transactionCurrency : '',
        transactionAmount : '',
    };

    this.client.AuthCreditCardFullEx(args, function(err, result) {
        if(err) return callback(err, 0);

        result = getFormattedResponseForAuthCard(camelcaseKeys(result));
        let responseCode = result.status;
        let resultMessage = transactionResultCodes[responseCode];

        // ResponeCode != 0, or is not a warning (041-047) => There was an error
        if(responseCode && (responseCode < 41 || responseCode > 47)) {
            return callback(new Error(resultMessage), responseCode);
        }

        callback(null, result);
    }, { timeout: 60000 });
};

PayPlusClient.prototype.issuePayment = function(options, callback) {
    options.paramJ = '4';

    this.authCreditCardEx(options, callback);
};

PayPlusClient.prototype.checkPayment = function(options, callback) {
    options.paramJ = '2'; // TODO Check if this is the best approach

    this.authCreditCardEx(options, callback);
};


/**
 * Request a credit card transaction
 * @param options An options object
 * @param callback A callback function (err, result)
 * @param action The retainer action, must be one of the following: ADD, EDIT, GET, DEL
 */

PayPlusClient.prototype.retainerAction = function(action, options, callback) {

    let ACTION_ADD = 'ADD',
        ACTION_EDIT = 'EDIT',
        ACTION_GET = 'GET',
        ACTION_DELETE = 'DEL';
    let validActions = [ACTION_ADD, ACTION_EDIT, ACTION_GET, ACTION_DELETE];

    if(validActions.indexOf(action) === -1) {
        return callback(new Error(`PayPlus Card Reatiner action must be one of the following: ${validActions.join()}`))
    }

    let args = {
        premiumUserName : this.user,
        premiumPassword : this.password,
        premiumCashierNum : this.cashierNumber,
        merchantNumber : this.merchantNumber,
        actionType : action,
        cardOrKey : options.cardNumber ? padCreditCard(options.cardNumber) : options.cardToken
    };

    let expMonth = options.expMonth || 0;
    let expYear = options.expYear || 0;

    if(action === ACTION_ADD || action === ACTION_EDIT) {
        if(expYear === 0 || expMonth === 0) {
            return callback(new Error('Missing one of the required parameters expYear, expMonth'));
        }

        let expDate = moment();
        // TODO FIX
        expDate.set('month', typeof options.expMonth === 'number' ? options.expMonth - 1 : options.expMonth);
        expDate.set('year', options.expYear.toString().length === 2 ? parseInt(`20${options.expYear}`) : options.expYear);
        args.expDate_MMYY = expDate.format('MMYY');
    } else {
        args.expDate_MMYY = '';
    }

    this.client.RetainerCreditCardStorage(args, function(err, result) {
        if(err) return callback(err, null);

        // Reformat result
        result = camelcaseKeys(result);
        let resultCode = parseInt(result['retainerCreditCardStorageResult']);

        // Success
        if(resultCode === 0) {

            let resultCard = result['resultCard'],
                resultExpDate = result['resultExpDate'],
                resultKey = result['resultKey'],
                resultExpMonth = resultExpDate.substring(0, 2),
                resultExpYear = resultExpDate.substring(2, 4);

            switch (action) {
                case ACTION_GET: return callback(null, {
                        action: action,
                        last4: resultCard.substring(resultCard.length - 4, resultCard.length),
                        type: getCardType(removeCardPadding(resultCard)),
                        expMonth: resultExpMonth,
                        expYear: resultExpYear
                    });
                case ACTION_ADD: return callback(null, {
                        action: action,
                        token: resultKey
                    });
                case ACTION_EDIT: return callback(null, {
                        action: action
                    });
                case ACTION_DELETE: return callback(null, {
                        action: action
                    });
            }
        } else {
            let errorMessage = retainerResultCodes[resultCode.toString()];
            callback(new Error(errorMessage), null);
        }

    });
};

/**
 * Store a credit card for retainer
 * @param cardNumber Card Number
 * @param expMonth Card's Expiration Month
 * @param expYear Card's Expiration Year
 * @param callback A callback function(err, result)
 */
PayPlusClient.prototype.storeCard = function(cardNumber, expMonth, expYear, callback) {
    let options = {
        cardNumber: cardNumber,
        expMonth: expMonth,
        expYear: expYear
    };
    this.retainerAction('ADD', options, callback);
};

/**
 * Retrieve retained card details
 * @param cardToken Retained card GUID token
 * @param callback A callback function(err, result)
 * @suppress unused
 */
PayPlusClient.prototype.getCard = function(cardToken, callback) {
    let options = {
        cardToken: cardToken
    };
    this.retainerAction('GET', options, callback);
};

/**
 * Edit a retained card's expiration date
 * @param cardToken Retained card GUID token
 * @param expMonth Expiration Month (MM)
 * @param expYear Expiration Year (YY)
 * @param callback A callback function(err, result)
 */
PayPlusClient.prototype.editCard = function(cardToken, expMonth, expYear, callback) {
    let options = {
        cardToken: cardToken,
        expMonth: expMonth,
        expYear: expYear
    };
    this.retainerAction('EDIT', options, callback);
};

/**
 * Delete a retained card
 * @param cardToken Retained card GUID token
 * @param callback A callback function(err, result)
 */
PayPlusClient.prototype.deleteCard = function(cardToken, callback) {
    let options = {
        cardToken: cardToken
    };
    this.retainerAction('DEL', options, callback);
};

/**
 * Initialize a new PayPlus client
 * @param {object} options Options object that must contain: username, password, cashierNum, merchantNum
 * as provided by PayPlus
 * @param {function} callback
 */
module.exports = function(options, callback) {

    const validationSchema = {
        username: Joi.string().required(),
        password: Joi.string().required(),
        cashierNum: Joi.string().required(),
        merchantNum: Joi.string().required()
    };

    const validationResult = Joi.validate(options, validationSchema);
    if(validationResult.error) {
        let err = validationResult.error;
        let message;
        if(err.details.length) {
            message = err.details[0].message;
        }
        return callback(new InvalidParametersError(message))
    } else {
        soap.createClient(config.wsUrl, function(err, client) {

            if(err) return callback(err);

            return callback(null, new PayPlusClient(client, {
                user: options.username,
                password: options.password,
                cashierNumber: options.cashierNum,
                merchantNumber: options.merchantNum
            }));
        });
    }
};